import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, Checkbox, FormControlLabel, InputAdornment, CircularProgress, MenuItem, useMediaQuery, useTheme, Grid, Chip, Alert } from '@mui/material';
import { Add, Delete, Edit, Search, FileUpload, LocationOn, PhotoLibrary, Timeline, Settings as SettingsIcon, Print as PrintIcon } from '@mui/icons-material';
import QRCode from 'qrcode';
import { CreateProductRequest, Product, ProductVariant } from '../types';
import api from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import CategoriesManagerModal from '../components/categories/CategoriesManagerModal';
import TextField from '../components/common/TextField';

// Clasificación para compras/análisis de gastos (independiente de la categoría comercial).
const PRODUCT_TYPES = ['Materia prima', 'Envases', 'Insumos y materiales', 'Gastos de fábrica', 'Producto final'];

const emptyProductForm = (): CreateProductRequest => ({
  name: '',
  description: '',
  sku: '',
  barcode: '',
  category: '',
  productType: '',
  price: 0,
  cost: 0,
  currentStock: 0,
  minStock: 0,
  maxStock: 0,
  reorderPoint: 0,
  imageUrl: '',
  additionalImages: [],
  isActive: true,
  trackStock: true,
  soldByWeight: false,
  unitOfMeasure: 'u',
});

export const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [products, setProducts] = useState<Product[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<CreateProductRequest>(emptyProductForm());
  const [additionalImagesText, setAdditionalImagesText] = useState('');
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const refreshCategories = async () => {
    try { const cats = await api.getCategories(); setCategories((cats.items || []).filter(c => c.isActive).sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name))); } catch {}
  };
  const [categories, setCategories] = useState<{ id: number; name: string; isActive: boolean; sortOrder: number }[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [locOpen, setLocOpen] = useState(false);
  const [locStocks, setLocStocks] = useState<{ location: string; stock: number }[]>([]);
  const [selected, setSelected] = useState<Product | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryProduct, setGalleryProduct] = useState<Product | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceData, setTraceData] = useState<any | null>(null);
  // Recipes viewing
  const [recipesOpen, setRecipesOpen] = useState(false);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipesProduct, setRecipesProduct] = useState<Product | null>(null);
  const [recipes, setRecipes] = useState<any[]>([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(null);
  const [schedule, setSchedule] = useState<{ tasks: Array<{ id: number; name: string; durationDays: number; startDay: number; endDay: number; isCritical: boolean }>; totalDurationDays: number } | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [cost, setCost] = useState<{ unitCostTotal: number; unitCostMaterials: number; unitCostServices: number; productPrice?: number; grossMargin?: number; grossMarginPct?: number } | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const galleryImages = useMemo(() => {
    if (!galleryProduct) return [];
    const all = [
      galleryProduct.imageUrl?.trim(),
      ...(galleryProduct.additionalImages || []),
    ].filter((url, idx, arr) => {
      if (!url) return false;
      const normalized = url.trim();
      if (!normalized) return false;
      return arr.findIndex((candidate) => candidate && candidate.trim() === normalized) === idx;
    }) as string[];
    return all;
  }, [galleryProduct]);

  const splitImageUrls = (value: string) => value
    .split(/[\r\n,;|]/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  const parseImagesField = (value?: string) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((img) => String(img).trim()).filter((img) => img.length > 0);
        }
      } catch {
        // fallback to delimiter parsing
      }
    }
    return splitImageUrls(trimmed);
  };

  const handleAdditionalImagesChange = (value: string) => {
    setAdditionalImagesText(value);
    setForm((prev) => ({ ...prev, additionalImages: splitImageUrls(value) }));
  };

  const hasProductImages = (product: Product) => {
    const imgs = [
      product.imageUrl?.trim(),
      ...(product.additionalImages || []),
    ].map((url) => (url || '').trim()).filter((url, idx, arr) => url && arr.indexOf(url) === idx);
    return imgs.length > 0;
  };

  const openGalleryDialog = (product: Product) => {
    if (!hasProductImages(product)) return;
    setGalleryProduct(product);
    setGalleryOpen(true);
  };

  const openTraceability = async (p: Product) => {
    try {
      setTraceLoading(true);
      const data = await api.getProductTraceability(p.id);
      setTraceData(data);
      setTraceOpen(true);
    } finally {
      setTraceLoading(false);
    }
  };

  // Etiqueta imprimible de un lote de producto terminado, con QR a la traza pública.
  const printLabel = async (b: any) => {
    const traceUrl = `${window.location.origin}/trace/${encodeURIComponent(b.publicId)}`;
    let qr = '';
    try { qr = await QRCode.toDataURL(traceUrl, { width: 220, margin: 1 }); } catch { /* sin QR si falla */ }
    const win = window.open('', '_blank', 'width=440,height=640');
    if (!win) { window.alert('Habilitá las ventanas emergentes para imprimir la etiqueta.'); return; }
    const esc = (s: any) => String(s ?? '').replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' } as any)[c]);
    const producido = b.producedAt ? new Date(b.producedAt).toLocaleDateString('es-AR') : '';
    win.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Etiqueta ${esc(b.batchCode || b.publicId)}</title>
      <style>
        *{box-sizing:border-box;font-family:Arial,Helvetica,sans-serif;}
        body{margin:0;padding:12px;}
        .label{width:320px;border:1px solid #000;border-radius:8px;padding:14px;margin:0 auto;}
        .name{font-size:16px;font-weight:700;margin-bottom:6px;line-height:1.2;}
        .row{font-size:13px;margin:2px 0;color:#222;}
        .loteCap{font-size:11px;color:#555;text-transform:uppercase;letter-spacing:1px;margin-top:6px;}
        .lote{font-size:22px;font-weight:800;letter-spacing:1px;margin:0 0 6px;}
        .qr{text-align:center;margin-top:8px;}
        .qr img{width:160px;height:160px;}
        .url{font-size:10px;color:#444;word-break:break-all;text-align:center;margin-top:4px;}
        .btn{display:block;margin:14px auto 0;padding:8px 16px;font-size:14px;cursor:pointer;}
        @media print{.btn{display:none;}body{padding:0;}}
      </style></head><body>
      <div class="label">
        <div class="name">${esc(traceData?.productName || '')}</div>
        <div class="loteCap">Lote</div>
        <div class="lote">${esc(b.batchCode || b.publicId)}</div>
        ${producido ? `<div class="row">Producido: ${esc(producido)}</div>` : ''}
        ${b.location ? `<div class="row">Ubicación: ${esc(b.location)}</div>` : ''}
        <div class="qr">${qr ? `<img src="${qr}" alt="QR trazabilidad"/>` : ''}</div>
        <div class="url">${esc(traceUrl)}</div>
      </div>
      <button class="btn" onclick="window.print()">Imprimir</button>
      <script>window.onload=function(){setTimeout(function(){try{window.print();}catch(e){}},250);};<\/script>
      </body></html>`);
    win.document.close();
  };

  const closeGalleryDialog = () => {
    setGalleryProduct(null);
    setGalleryOpen(false);
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.getProducts({ page, pageSize, search });
      setProducts(res.products);
      setTotalPages(res.totalPages || 1);
      try { const cats = await api.getCategories(); setCategories((cats.items || []).filter(c => c.isActive).sort((a,b)=> (a.sortOrder-b.sortOrder) || a.name.localeCompare(b.name))); } catch {}
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useDebounce(search, 300);
  const isFirstRender = useRef(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const onSearch = async () => {
    setPage(1);
    await load();
  };

  const resetForm = () => {
    setForm(emptyProductForm());
    setAdditionalImagesText('');
    setVariants([]);
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setFormError(null);
    setOpenDialog(true);
  };

  const hasVariants = variants.some(v => (v.label || '').trim().length > 0);
  const variantsStockTotal = variants.reduce((acc, v) => acc + (Number(v.stock) || 0), 0);

  const addVariant = () => setVariants(prev => [...prev, { label: '', stock: 0, barcode: '', isActive: true }]);
  const updateVariant = (idx: number, patch: Partial<ProductVariant>) =>
    setVariants(prev => prev.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  const removeVariant = (idx: number) => setVariants(prev => prev.filter((_, i) => i !== idx));

  const openEdit = (p: Product) => {
    setEditing(p);
    setVariants([]);
    api.getProductVariants(p.id).then(vs => setVariants(vs || [])).catch(() => setVariants([]));
    setForm({
      name: p.name,
      description: p.description || '',
      sku: p.sku,
      barcode: p.barcode || '',
      category: p.category || '',
      productType: p.productType || '',
      price: p.price,
      cost: p.cost,
      currentStock: p.currentStock,
      minStock: p.minStock,
      maxStock: p.maxStock,
      reorderPoint: p.reorderPoint,
      imageUrl: p.imageUrl || '',
      additionalImages: p.additionalImages || [],
      isActive: p.isActive,
      trackStock: p.trackStock,
      soldByWeight: p.soldByWeight ?? false,
      unitOfMeasure: p.unitOfMeasure || 'u',
    });
    setAdditionalImagesText((p.additionalImages || []).join('\n'));
    setFormError(null);
    setOpenDialog(true);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.sku.trim()) {
      setFormError('Completá al menos Nombre y SKU.');
      return;
    }
    // Si hay variantes, el stock del producto pasa a ser la suma de las variantes.
    const cleanVariants = variants.filter(v => (v.label || '').trim().length > 0);
    const payload = cleanVariants.length > 0 ? { ...form, currentStock: variantsStockTotal } : form;
    setSaving(true);
    setFormError(null);
    let productId = editing?.id;
    try {
      if (editing) {
        await api.updateProduct(editing.id, payload);
      } else {
        const created = await api.createProduct(payload);
        productId = created.id;
      }
    } catch (e: any) {
      // El backend puede responder de dos formas y antes solo mostrábamos la primera:
      //  - { message }: error del controller (ej. "El SKU \"X\" ya lo usa el producto Y").
      //  - { errors: { SKU: [...] }, title }: validación de modelo de [ApiController]
      //    (ej. SKU/Barcode > 50 chars, SKU requerido). Este 400 corta ANTES del cuerpo
      //    del controller, no loguea nada, y antes caía en el genérico "No se pudo guardar"
      //    dejando al usuario sin saber la causa real.
      const data = e?.response?.data;
      let msg: string | undefined = data?.message;
      if (!msg && data?.errors && typeof data.errors === 'object') {
        const all = Object.values(data.errors as Record<string, string[]>).flat();
        if (all.length) msg = all.join(' ');
      }
      setFormError(msg || data?.title || 'No se pudo guardar el producto.');
      setSaving(false);
      return;
    }
    if (productId) {
      try { await api.setProductVariants(productId, cleanVariants); } catch {}
    }
    setSaving(false);
    setOpenDialog(false);
    await load();
  };

  const remove = async (p: Product) => {
    if (!window.confirm(`¿Está seguro que desea eliminar "${p.name}"?`)) return;
    await api.deleteProduct(p.id);
    await load();
  };

  const openRecipes = async (p: Product) => {
    setRecipesProduct(p);
    setRecipesOpen(true);
    setRecipesLoading(true);
    try {
      const res = await api.getRecipes({ page: 1, pageSize: 100, productId: p.id });
      setRecipes(res.recipes || []);
      const first = (res.recipes || [])[0];
      if (first) {
        await loadRecipeInsights(first.id);
        setSelectedRecipeId(first.id);
      } else {
        setSelectedRecipeId(null);
        setSchedule(null);
        setCost(null);
      }
    } finally {
      setRecipesLoading(false);
    }
  };

  const loadRecipeInsights = async (recipeId: number) => {
    setScheduleLoading(true);
    setCostLoading(true);
    try {
      const sch = await api.getRecipeSchedule(recipeId);
      setSchedule({ tasks: sch.tasks || [], totalDurationDays: sch.totalDurationDays || 0 });
    } catch {
      setSchedule(null);
    } finally {
      setScheduleLoading(false);
    }
    try {
      const c = await api.getRecipeCost(recipeId, 1);
      setCost({
        unitCostTotal: Number(c.unitCostTotal || 0),
        unitCostMaterials: Number(c.unitCostMaterials || 0),
        unitCostServices: Number(c.unitCostServices || 0),
        productPrice: c.productPrice,
        grossMargin: c.grossMargin,
        grossMarginPct: c.grossMarginPct,
      });
    } catch {
      setCost(null);
    } finally {
      setCostLoading(false);
    }
  };

  const parseCsv = (text: string) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return [] as any[];
    const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const dataLines = lines.slice(1);
    const idx = (name: string) => header.indexOf(name);
    const items: any[] = [];
    for (const line of dataLines) {
      const cols = line.match(/\s*([^",]+|"[^"]*")(?:,|$)/g)?.map(x => x.replace(/,$/, '').trim()) || line.split(',');
      const val = (i: number) => {
        if (i < 0 || i >= cols.length) return '';
        return cols[i]?.replace(/^"|"$/g, '') || '';
      };
      const name = val(idx('name')) || val(idx('nombre'));
      const sku = val(idx('sku'));
      if (!name && !sku) continue;
      items.push({
        name,
        sku,
        category: val(idx('category')) || val(idx('categoria')),
        price: Number(val(idx('price')) || 0),
        cost: Number(val(idx('cost')) || 0),
        currentStock: Number(val(idx('currentstock')) || val(idx('stock')) || 0),
        minStock: Number(val(idx('minstock')) || 0),
        reorderPoint: Number(val(idx('reorderpoint')) || 0),
        maxStock: Number(val(idx('maxstock')) || 0),
        description: val(idx('description')) || '',
        barcode: val(idx('barcode')) || '',
        imageUrl: val(idx('imageurl')) || '',
        additionalImages: parseImagesField(val(idx('additionalimages'))),
        isActive: (val(idx('isactive')) || 'true').toLowerCase() !== 'false',
        trackStock: (val(idx('trackstock')) || 'true').toLowerCase() !== 'false',
      });
    }
    return items;
  };

  const onCsvFile = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
  };

  const runImport = async () => {
    const items = parseCsv(csvText);
    if (items.length === 0) {
      setImportSummary('No se detectaron filas válidas. Verifica el CSV.');
      return;
    }
    const res = await api.bulkImportProducts(items);
    setImportSummary(`Creados: ${res.created} • Actualizados: ${res.updated} • Omitidos: ${res.skipped}${res.errors?.length ? ' • Errores: ' + res.errors.length : ''}`);
    await load();
  };

  const StockBadge: React.FC<{ p: Product }> = ({ p }) => {
    const color = p.stockStatus === 'CRITICAL' ? 'error.main' : p.stockStatus === 'LOW' ? 'warning.main' : p.stockStatus === 'WARNING' ? 'warning.main' : 'success.main';
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: 1, bgcolor: `${color}22`, color }}>
        {p.currentStock}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Productos</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" size={isMobile ? 'small' : 'medium'} startIcon={<FileUpload />} onClick={() => { setImportOpen(true); setImportSummary(null); }}>Importar</Button>
          <Button variant="outlined" size={isMobile ? 'small' : 'medium'} onClick={async () => { const blob = await api.downloadProductsCsv(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'products.csv'; a.click(); window.URL.revokeObjectURL(url); }}>Exportar</Button>
          <Button variant="contained" size={isMobile ? 'small' : 'medium'} startIcon={<Add />} onClick={openCreate}>Nuevo</Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Buscar por nombre o SKU" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSearch()} InputProps={{ startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>) }} sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={onSearch}>Buscar</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>SKU</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Categoría</TableCell>
              <TableCell align="right">PVP</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Costo</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Margen $</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Margen %</TableCell>
              <TableCell align="center">Stock</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Full</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{p.sku}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{p.name}</Typography>
                    <Tooltip title="Ver por almacén">
                      <IconButton size="small" onClick={async () => { setSelected(p); const s = await api.getLocationStocks(p.id); setLocStocks(s.items || []); setLocOpen(true); }}>
                        <LocationOn fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{p.category}</TableCell>
                <TableCell align="right">${p.price.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>${p.cost.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>${(p.price - p.cost).toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{p.price > 0 ? (((p.price - p.cost)/p.price)*100).toFixed(0) + '%' : '—'}</TableCell>
                <TableCell align="center"><StockBadge p={p} /></TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {p.mlFullStock > 0 ? (
                    <Chip size="small" label={p.mlFullStock} color="info" variant="outlined" />
                  ) : '—'}
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{p.stockStatus}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver plan (formulaciones + Gantt)">
                    <Button size="small" sx={{ mr: 1 }} onClick={() => openRecipes(p)}>Plan (Gantt)</Button>
                  </Tooltip>
                  <Tooltip title="Ver galería">
                    <span>
                      <IconButton size="small" onClick={() => openGalleryDialog(p)} disabled={!hasProductImages(p)}>
                        <PhotoLibrary fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Trazabilidad de producción">
                    <IconButton size="small" onClick={() => openTraceability(p)}>
                      <Timeline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(p)}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" onClick={() => remove(p)}><Delete /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
      </Stack>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <TextField fullWidth label="SKU" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
              <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ width: '100%' }}>
                <TextField select fullWidth label="Categoría" value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {categories.map(c => (
                    <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                  ))}
                  {!categories.length && (
                    <MenuItem value="">—</MenuItem>
                  )}
                </TextField>
                <Tooltip title="Gestionar categorías">
                  <IconButton sx={{ mt: 0.5 }} onClick={() => setCategoriesModalOpen(true)}><SettingsIcon /></IconButton>
                </Tooltip>
              </Stack>
              <TextField select fullWidth label="Tipo" value={form.productType || ''} onChange={e => setForm({ ...form, productType: e.target.value })}
                helperText="Para compras y análisis de gastos">
                <MenuItem value="">—</MenuItem>
                {PRODUCT_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField fullWidth multiline minRows={2} label="Descripción" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Código de barras" value={form.barcode || ''} onChange={e => setForm({ ...form, barcode: e.target.value })} />
              <TextField fullWidth label="URL de imagen principal" value={form.imageUrl || ''} onChange={e => setForm({ ...form, imageUrl: e.target.value })} placeholder="https://..." />
            </Stack>
            <TextField fullWidth label="URLs de galería (una por línea o separadas por comas)" value={additionalImagesText} onChange={e => handleAdditionalImagesChange(e.target.value)} helperText="Se usará para la galería de imágenes del producto." multiline minRows={2} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth type="number" label="PVP" value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value) })} />
              <TextField fullWidth type="number" label="Costo" value={form.cost} onChange={e => setForm({ ...form, cost: Number(e.target.value) })} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth type="number" label="Stock actual" value={hasVariants ? variantsStockTotal : form.currentStock} onChange={e => setForm({ ...form, currentStock: Number(e.target.value) })} disabled={hasVariants} helperText={hasVariants ? 'Calculado como suma de las variantes' : undefined} />
              <TextField fullWidth type="number" label="Stock mínimo" value={form.minStock} onChange={e => setForm({ ...form, minStock: Number(e.target.value) })} />
              <TextField fullWidth type="number" label="Punto de reposición" value={form.reorderPoint} onChange={e => setForm({ ...form, reorderPoint: Number(e.target.value) })} />
              <TextField fullWidth type="number" label="Stock máximo" value={form.maxStock} onChange={e => setForm({ ...form, maxStock: Number(e.target.value) })} />
            </Stack>

            {/* Variantes / Talles */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Variantes / Talles</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Usá variantes para manejar talles, colores o presentaciones con stock propio. El precio se mantiene igual para todas.
                  </Typography>
                </Box>
                <Button size="small" startIcon={<Add />} onClick={addVariant}>Agregar variante</Button>
              </Stack>
              {variants.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin variantes. El stock se gestiona desde el campo de stock principal.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {variants.map((v, idx) => (
                    <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                      <TextField size="small" fullWidth label="Variante" placeholder="ej. talle 42" value={v.label} onChange={e => updateVariant(idx, { label: e.target.value })} />
                      <TextField size="small" label="Código de barras" value={v.barcode || ''} onChange={e => updateVariant(idx, { barcode: e.target.value })} />
                      <TextField size="small" type="number" label="Stock" sx={{ width: 120 }} value={v.stock} onChange={e => updateVariant(idx, { stock: Number(e.target.value) })} />
                      <IconButton size="small" onClick={() => removeVariant(idx)}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                  <Typography variant="caption" color="text.secondary">Stock total: {variantsStockTotal}</Typography>
                </Stack>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField select label="Unidad de medida" sx={{ minWidth: 200 }} value={form.unitOfMeasure || 'u'}
                onChange={e => { const u = e.target.value; setForm({ ...form, unitOfMeasure: u, soldByWeight: u !== 'u' }); }}
                helperText="Usá la unidad real (u) o por peso (kg/g) para balanza.">
                <MenuItem value="u">Unitario (u)</MenuItem>
                <MenuItem value="kg">Kilogramo (kg)</MenuItem>
                <MenuItem value="g">Gramo (g)</MenuItem>
              </TextField>
              <FormControlLabel control={<Checkbox checked={!!form.soldByWeight} onChange={e => setForm({ ...form, soldByWeight: e.target.checked })} />} label="Vendido por peso (balanza)" />
            </Stack>
            <FormControlLabel control={<Checkbox checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activo" />
            <FormControlLabel control={<Checkbox checked={form.trackStock} onChange={e => setForm({ ...form, trackStock: e.target.checked })} />} label="Controlar stock" />
          </Stack>
        </DialogContent>
        {formError && (
          <Box sx={{ px: 3, pt: 1 }}>
            <Alert severity="error" onClose={() => setFormError(null)}>{formError}</Alert>
          </Box>
        )}
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={submit} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>{editing ? 'Guardar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importar productos desde CSV</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Formato recomendado (encabezados): name,sku,category,price,cost,currentStock,minStock,reorderPoint,maxStock,description,barcode,imageUrl,additionalImages,isActive,trackStock
            </Typography>
            <Button component="label" variant="outlined" startIcon={<FileUpload />}>
              Seleccionar archivo CSV
              <input hidden type="file" accept=".csv,text/csv" onChange={(e) => e.target.files && e.target.files[0] && onCsvFile(e.target.files[0])} />
            </Button>
            <TextField label="o pegar CSV aquí" value={csvText} onChange={e => setCsvText(e.target.value)} multiline minRows={6} />
            <Button variant="contained" onClick={runImport}>Importar</Button>
            {importSummary && <Typography variant="body2">{importSummary}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={traceOpen} onClose={() => { setTraceOpen(false); setTraceData(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Trazabilidad de producto</DialogTitle>
        <DialogContent dividers>
          {traceLoading && (
            <Typography variant="body2">Cargando trazabilidad...</Typography>
          )}
          {!traceLoading && traceData && (
            <Box>
              <Typography variant="h6" gutterBottom>{traceData.productName}</Typography>
              {(traceData.batches || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No hay lotes registrados para este producto.</Typography>
              )}
              {(traceData.batches || []).map((b: any) => (
                <Card key={b.publicId} sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">Lote: {b.batchCode || b.publicId}</Typography>
                      <Button size="small" startIcon={<PrintIcon />} onClick={() => printLabel(b)}>Imprimir etiqueta</Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Stock actual: {b.quantity} · Costo unitario: ${b.unitCost?.toLocaleString?.() ?? b.unitCost} · Ubicación: {b.location}
                    </Typography>
                    {b.productionOrderId && (
                      <Typography variant="body2" color="text.secondary">
                        OP origen: {b.productionOrderNumber || `#${b.productionOrderId}`} {b.producedAt ? `· Producido: ${new Date(b.producedAt).toLocaleString()}` : ''}
                      </Typography>
                    )}
                    {(b.components || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Componentes usados:</Typography>
                        {(b.components || []).map((c: any, idx: number) => (
                          <Box key={idx} sx={{ ml: 1, mt: 0.5 }}>
                            <Typography variant="body2">
                              - [{c.type}] {c.name} · Req: {c.quantityRequired}
                            </Typography>
                            {(c.batches || []).length > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                Lotes: {(c.batches || []).map((cb: any) => {
                                  const baseLabel = `${cb.lotCode || cb.batchPublicId} (qty ${cb.quantityUsed})`;
                                  const extra: string[] = [];
                                  if (cb.supplierName) extra.push(`Prov: ${cb.supplierName}`);
                                  if (cb.purchaseReference) extra.push(`Ref: ${cb.purchaseReference}`);
                                  if (cb.expirationDate) extra.push(`Vence: ${new Date(cb.expirationDate).toLocaleDateString()}`);
                                  return extra.length ? `${baseLabel} [${extra.join(' · ')}]` : baseLabel;
                                }).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTraceOpen(false); setTraceData(null); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={locOpen} onClose={() => setLocOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Stock por almacén — {selected?.name}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Almacén</TableCell>
                  <TableCell align="right">Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locStocks.map((s, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{s.location}</TableCell>
                    <TableCell align="right">{s.stock}</TableCell>
                  </TableRow>
                ))}
                {locStocks.length === 0 && (
                  <TableRow><TableCell colSpan={2}><Typography variant="body2" color="text.secondary">Sin stock en almacenes</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={galleryOpen} onClose={closeGalleryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Galería — {galleryProduct?.name}</DialogTitle>
        <DialogContent>
          {galleryImages.length ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {galleryImages.map((url, idx) => (
                <Box key={`${url}-${idx}`} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                  <Box component="img" src={url} alt={`Imagen ${idx + 1}`} loading="lazy" sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', backgroundColor: '#f8f8f8' }} />
                  <Box sx={{ px: 1, py: 0.5 }}>
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{url}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Sin imágenes registradas.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGalleryDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Recipes for selected product */}
      <Dialog open={recipesOpen} onClose={() => setRecipesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Formulaciones — {recipesProduct?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">Listado de formulaciones del producto</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={async () => { if (!recipesProduct) return; await openRecipes(recipesProduct); }}>Actualizar</Button>
                <Button size="small" variant="contained" onClick={() => recipesProduct && navigate(`/recipes?new=1&productId=${recipesProduct.id}`)}>Nueva formulación</Button>
              </Stack>
            </Stack>
            {recipesLoading ? (
              <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="right">Tiempo (hs)</TableCell>
                      <TableCell>Notas</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipes.map((r: any) => (
                      <TableRow key={r.id} hover selected={selectedRecipeId === r.id}>
                        <TableCell>{r.name}</TableCell>
                        <TableCell align="right">{Number(r.manufacturingTimeHours || 0)}</TableCell>
                        <TableCell>{r.notes || '—'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => navigate(`/recipes?edit=${r.id}`)}>Editar</Button>
                          <Button size="small" variant={selectedRecipeId === r.id ? 'contained' : 'outlined'} sx={{ ml: 1 }} onClick={async () => { setSelectedRecipeId(r.id); await loadRecipeInsights(r.id); }}>Ver Gantt</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {recipes.length === 0 && (
                      <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">Sin formulaciones para este producto</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {selectedRecipeId && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Plan de producción (Gantt)</Typography>
                {scheduleLoading ? (
                  <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={20} /></Stack>
                ) : schedule ? (
                  <Box sx={{ p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" gutterBottom>Tiempo total: <strong>{schedule.totalDurationDays?.toFixed(2)} días</strong></Typography>
                    <Box sx={{ position: 'relative', height: Math.max(1, schedule.tasks.length) * 28, overflowX: 'auto', overflowY: 'hidden' }}>
                      {schedule.tasks.map((t, i) => (
                        <Box key={t.id} sx={{ position: 'absolute', left: `${t.startDay*30}px`, top: `${i*26}px`, width: `${Math.max(0.5, t.durationDays)*30}px`, height: 20, bgcolor: t.isCritical ? 'error.main' : 'primary.main', opacity: 0.85, borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#fff', px: 0.5, whiteSpace: 'nowrap' }}>{t.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No se pudo cargar el Gantt.</Typography>
                )}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }} gutterBottom>Métricas</Typography>
                {costLoading ? (
                  <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={20} /></Stack>
                ) : cost ? (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">Costo unitario</Typography>
                      <Typography variant="h6">${Number(cost.unitCostTotal).toFixed(2)}</Typography>
                      <Typography variant="caption" color="text.secondary">Mat.: ${Number(cost.unitCostMaterials).toFixed(2)} • Serv.: ${Number(cost.unitCostServices).toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">PVP</Typography>
                      <Typography variant="h6">${Number(cost.productPrice ?? recipesProduct?.price ?? 0).toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">Margen</Typography>
                      <Typography variant="h6">${Number(cost.grossMargin ?? ((recipesProduct?.price || 0) - (cost.unitCostTotal || 0))).toFixed(2)}</Typography>
                      <Typography variant="caption" color="text.secondary">{Number(cost.grossMarginPct ?? (((recipesProduct?.price || 0) - (cost.unitCostTotal || 0)) / Math.max(1, (recipesProduct?.price || 0)))).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                    </Box>
                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">Lead total</Typography>
                      <Typography variant="h6">{schedule?.totalDurationDays?.toFixed(2) ?? '—'} días</Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No se pudieron calcular las métricas.</Typography>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipesOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CategoriesManagerModal open={categoriesModalOpen} onClose={() => setCategoriesModalOpen(false)} onChanged={refreshCategories} />
    </Box>
  );
};
